import { Component, computed, inject, OnInit, signal, HostListener } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ReferralsRepositoryService,
  Referral,
  AcceptReferralRequest,
  AcceptReferralClinicalData,
  PatientRepositoryService,
  Patient,
  MedicationRepositoryService,
  Medication,
  AllergiesRepositoryService,
  Allergy,
  ProblemListRepositoryService,
  Problem,
  DiagnosisCodesRepositoryService,
  PatientDiagnosisCode
} from '@xmedical/core';
import {
  ClinicalDataMergeSectionComponent,
  MergeDataItem,
  MergeSelectionChangeEvent
} from '../clinical-data-merge-section/clinical-data-merge-section.component';

type ReferralDirection = 'incoming' | 'outgoing';

@Component({
  selector: 'xm-accept-referral-merge',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ClinicalDataMergeSectionComponent
  ],
  providers: [DatePipe],
  templateUrl: './accept-referral-merge.component.html',
  styleUrl: './accept-referral-merge.component.scss'
})
export class AcceptReferralMergeComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private referralsRepo = inject(ReferralsRepositoryService);
  private patientRepo = inject(PatientRepositoryService);
  private medicationRepo = inject(MedicationRepositoryService);
  private allergyRepo = inject(AllergiesRepositoryService);
  private problemRepo = inject(ProblemListRepositoryService);
  private diagnosisRepo = inject(DiagnosisCodesRepositoryService);
  private snackBar = inject(MatSnackBar);
  private datePipe = inject(DatePipe);

  // Route params
  direction = signal<ReferralDirection>('incoming');
  referralId = signal<number>(0);
  patientProfileId = signal<number>(0);

  // Data
  referral = signal<Referral | null>(null);
  existingPatient = signal<Patient | null>(null);
  isLoading = signal(true);
  error = signal<string | null>(null);

  // Existing patient clinical data
  existingMedications = signal<Medication[]>([]);
  existingAllergies = signal<Allergy[]>([]);
  existingProblems = signal<Problem[]>([]);
  existingDiagnosis = signal<PatientDiagnosisCode[]>([]);

  // Selection state - items to ADD from referral
  medicationsToAdd = signal<Set<number>>(new Set());
  allergiesToAdd = signal<Set<number>>(new Set());
  problemsToAdd = signal<Set<number>>(new Set());
  diagnosisToAdd = signal<Set<string>>(new Set());

  // Selection state - items to REMOVE from existing patient
  medicationsToRemove = signal<Set<number>>(new Set());
  allergiesToRemove = signal<Set<number>>(new Set());
  problemsToRemove = signal<Set<number>>(new Set());
  diagnosisToRemove = signal<Set<string>>(new Set());

  // Submitting
  isSubmitting = signal(false);

  // Scroll state for action bar shadow
  isScrolled = signal(false);

  @HostListener('window:scroll')
  onScroll(): void {
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    // Show shadow when not at the bottom (with some threshold)
    const isAtBottom = scrollPosition + windowHeight >= documentHeight - 20;
    this.isScrolled.set(!isAtBottom);
  }

  // Computed: Patient snapshot from referral
  patientSnapshot = computed(() => this.referral()?.referringPatientSnapshot || null);

  // Computed: Patient name
  patientName = computed(() => {
    const p = this.existingPatient();
    if (!p) return 'Unknown Patient';
    return p.fullName || `${p.firstName || ''} ${p.surname || ''}`.trim() || 'Unknown Patient';
  });

  // Computed: Profile ID display
  profileIdDisplay = computed(() => {
    const p = this.existingPatient();
    return p?.profileId || this.patientProfileId();
  });

  // Convert existing patient data to MergeDataItem format
  existingMedicationItems = computed<MergeDataItem[]>(() => {
    return this.existingMedications().map(m => ({
      id: m.id,
      primary: m.description,
      secondary: m.dosage ? `Dosage: ${m.dosage}` : undefined
    }));
  });

  existingAllergyItems = computed<MergeDataItem[]>(() => {
    return this.existingAllergies().map(a => ({
      id: a.id,
      primary: a.allergy,
      secondary: a.comment || undefined
    }));
  });

  existingProblemItems = computed<MergeDataItem[]>(() => {
    return this.existingProblems().map(p => ({
      id: p.id,
      primary: p.problem,
      secondary: p.comment || undefined
    }));
  });

  existingDiagnosisItems = computed<MergeDataItem[]>(() => {
    return this.existingDiagnosis().map(d => ({
      id: d.icD10Code,
      primary: d.icD10Code,
      secondary: d.comment || undefined
    }));
  });

  // Convert referral snapshot data to MergeDataItem format
  referralMedicationItems = computed<MergeDataItem[]>(() => {
    const meds = this.patientSnapshot()?.medication || [];
    return meds.map(m => ({
      id: m.id,
      primary: m.description,
      secondary: m.dosage ? `Dosage: ${m.dosage}` : undefined
    }));
  });

  referralAllergyItems = computed<MergeDataItem[]>(() => {
    const allergies = this.patientSnapshot()?.allergies || [];
    return allergies.map(a => ({
      id: a.id,
      primary: a.allergy,
      secondary: a.comment || undefined
    }));
  });

  referralProblemItems = computed<MergeDataItem[]>(() => {
    const problems = this.patientSnapshot()?.problemList || [];
    return problems.map(p => ({
      id: p.id,
      primary: p.problem,
      secondary: p.comment || undefined
    }));
  });

  referralDiagnosisItems = computed<MergeDataItem[]>(() => {
    const diagnosis = this.patientSnapshot()?.diagnosis || [];
    return diagnosis.map(d => ({
      id: d.icD10Code,
      primary: d.icD10Code,
      secondary: d.comment || undefined
    }));
  });

  // Computed: Sets of existing item IDs for comparison
  existingMedicationIds = computed(() => {
    // Compare by description (normalized) since IDs are different across practices
    const descriptions = new Set(this.existingMedications().map(m => m.description.toLowerCase().trim()));
    const referralMeds = this.patientSnapshot()?.medication || [];
    return new Set(referralMeds.filter(m => descriptions.has(m.description.toLowerCase().trim())).map(m => m.id));
  });

  existingAllergyIds = computed(() => {
    const names = new Set(this.existingAllergies().map(a => a.allergy.toLowerCase().trim()));
    const referralAllergies = this.patientSnapshot()?.allergies || [];
    return new Set(referralAllergies.filter(a => names.has(a.allergy.toLowerCase().trim())).map(a => a.id));
  });

  existingProblemIds = computed(() => {
    const names = new Set(this.existingProblems().map(p => p.problem.toLowerCase().trim()));
    const referralProblems = this.patientSnapshot()?.problemList || [];
    return new Set(referralProblems.filter(p => names.has(p.problem.toLowerCase().trim())).map(p => p.id));
  });

  existingDiagnosisIds = computed(() => {
    const codes = new Set(this.existingDiagnosis().map(d => d.icD10Code));
    const referralDiagnosis = this.patientSnapshot()?.diagnosis || [];
    return new Set(referralDiagnosis.filter(d => codes.has(d.icD10Code)).map(d => d.icD10Code));
  });

  // Computed: Has any data to show
  hasAnyData = computed(() => {
    return this.existingMedicationItems().length > 0 ||
           this.referralMedicationItems().length > 0 ||
           this.existingAllergyItems().length > 0 ||
           this.referralAllergyItems().length > 0 ||
           this.existingProblemItems().length > 0 ||
           this.referralProblemItems().length > 0 ||
           this.existingDiagnosisItems().length > 0 ||
           this.referralDiagnosisItems().length > 0;
  });

  ngOnInit(): void {
    const direction = this.route.snapshot.paramMap.get('direction') as ReferralDirection;
    const referralId = Number(this.route.snapshot.paramMap.get('id'));
    const patientProfileId = Number(this.route.snapshot.paramMap.get('patientId'));

    if (!direction || !['incoming', 'outgoing'].includes(direction)) {
      this.error.set('Invalid referral direction');
      this.isLoading.set(false);
      return;
    }

    if (!referralId || isNaN(referralId)) {
      this.error.set('Invalid referral ID');
      this.isLoading.set(false);
      return;
    }

    if (!patientProfileId || isNaN(patientProfileId)) {
      this.error.set('Invalid patient ID');
      this.isLoading.set(false);
      return;
    }

    this.direction.set(direction);
    this.referralId.set(referralId);
    this.patientProfileId.set(patientProfileId);
    this.loadData();
  }

  // Selection change handlers
  onMedicationSelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.medicationsToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as number);
        } else {
          newSet.delete(event.id as number);
        }
        return newSet;
      });
    } else {
      this.medicationsToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as number);
        } else {
          newSet.delete(event.id as number);
        }
        return newSet;
      });
    }
  }

  onAllergySelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.allergiesToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as number);
        } else {
          newSet.delete(event.id as number);
        }
        return newSet;
      });
    } else {
      this.allergiesToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as number);
        } else {
          newSet.delete(event.id as number);
        }
        return newSet;
      });
    }
  }

  onProblemSelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.problemsToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as number);
        } else {
          newSet.delete(event.id as number);
        }
        return newSet;
      });
    } else {
      this.problemsToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as number);
        } else {
          newSet.delete(event.id as number);
        }
        return newSet;
      });
    }
  }

  onDiagnosisSelectionChange(event: MergeSelectionChangeEvent): void {
    if (event.type === 'add') {
      this.diagnosisToAdd.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as string);
        } else {
          newSet.delete(event.id as string);
        }
        return newSet;
      });
    } else {
      this.diagnosisToRemove.update(set => {
        const newSet = new Set(set);
        if (event.selected) {
          newSet.add(event.id as string);
        } else {
          newSet.delete(event.id as string);
        }
        return newSet;
      });
    }
  }

  // Accept and merge
  acceptAndMerge(): void {
    if (this.isSubmitting()) return;

    const patient = this.existingPatient();
    if (!patient) {
      this.snackBar.open('No patient selected', 'Dismiss', { duration: 3000 });
      return;
    }

    this.isSubmitting.set(true);

    const clinicalData: AcceptReferralClinicalData = {
      addMedicationIds: Array.from(this.medicationsToAdd()),
      addProblemIds: Array.from(this.problemsToAdd()),
      addAllergyIds: Array.from(this.allergiesToAdd()),
      addDiagnosisCodes: Array.from(this.diagnosisToAdd()),
      removeMedicationIds: Array.from(this.medicationsToRemove()),
      removeProblemIds: Array.from(this.problemsToRemove()),
      removeAllergyIds: Array.from(this.allergiesToRemove()),
      removeDiagnosisCodes: Array.from(this.diagnosisToRemove())
    };

    const request: AcceptReferralRequest = {
      receivingPatientId: patient.id,
      clinicalData
    };

    this.referralsRepo.accept(this.referralId(), request).subscribe({
      next: (result) => {
        this.isSubmitting.set(false);
        this.snackBar.open('Referral accepted and merged successfully', 'Dismiss', { duration: 3000 });
        this.router.navigate(['/patients', result.profileId]);
      },
      error: (err) => {
        console.error('Failed to accept referral:', err);
        this.snackBar.open('Failed to accept referral', 'Dismiss', { duration: 3000 });
        this.isSubmitting.set(false);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/referrals', this.direction(), this.referralId()]);
  }

  private loadData(): void {
    this.isLoading.set(true);
    this.error.set(null);

    // Load referral and patient data in parallel
    forkJoin({
      referral: this.referralsRepo.getByIdWithPatient(this.referralId()),
      patient: this.patientRepo.loadByProfileId(this.patientProfileId())
    }).subscribe({
      next: ({ referral, patient }) => {
        this.referral.set(referral);
        this.existingPatient.set(patient);

        // Load patient clinical data
        this.loadPatientClinicalData(patient.id);
      },
      error: (err) => {
        console.error('Failed to load data:', err);
        this.error.set('Failed to load referral or patient data');
        this.isLoading.set(false);
      }
    });
  }

  private loadPatientClinicalData(patientId: number): void {
    forkJoin({
      medications: this.medicationRepo.loadPatientMedication(patientId),
      allergies: this.allergyRepo.loadPatientAllergyList(patientId),
      problems: this.problemRepo.loadPatientProblemList(patientId),
      diagnosis: this.diagnosisRepo.loadPatientDiagnosisCodes(patientId)
    }).subscribe({
      next: ({ medications, allergies, problems, diagnosis }) => {
        this.existingMedications.set(medications);
        this.existingAllergies.set(allergies);
        this.existingProblems.set(problems);
        this.existingDiagnosis.set(diagnosis);

        // Initialize selections - pre-select all NEW items to add
        this.initializeSelections();
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load clinical data:', err);
        // Don't fail completely - just set empty arrays
        this.existingMedications.set([]);
        this.existingAllergies.set([]);
        this.existingProblems.set([]);
        this.existingDiagnosis.set([]);
        this.initializeSelections();
        this.isLoading.set(false);
      }
    });
  }

  private initializeSelections(): void {
    // Pre-select all NEW referral items to add
    const existingMedIds = this.existingMedicationIds();
    const newMedIds = new Set(
      this.referralMedicationItems()
        .filter(m => !existingMedIds.has(m.id as number))
        .map(m => m.id as number)
    );
    this.medicationsToAdd.set(newMedIds);

    const existingAllergyIds = this.existingAllergyIds();
    const newAllergyIds = new Set(
      this.referralAllergyItems()
        .filter(a => !existingAllergyIds.has(a.id as number))
        .map(a => a.id as number)
    );
    this.allergiesToAdd.set(newAllergyIds);

    const existingProblemIds = this.existingProblemIds();
    const newProblemIds = new Set(
      this.referralProblemItems()
        .filter(p => !existingProblemIds.has(p.id as number))
        .map(p => p.id as number)
    );
    this.problemsToAdd.set(newProblemIds);

    const existingDiagIds = this.existingDiagnosisIds();
    const newDiagCodes = new Set(
      this.referralDiagnosisItems()
        .filter(d => !existingDiagIds.has(d.id as string))
        .map(d => d.id as string)
    );
    this.diagnosisToAdd.set(newDiagCodes);

    // Pre-select items for removal (items in patient but not in referral)
    const referralMedNames = new Set(
      this.referralMedicationItems().map(m => m.primary.toLowerCase().trim())
    );
    const medsToRemove = new Set(
      this.existingMedicationItems()
        .filter(m => !referralMedNames.has(m.primary.toLowerCase().trim()))
        .map(m => m.id as number)
    );
    this.medicationsToRemove.set(medsToRemove);

    const referralAllergyNames = new Set(
      this.referralAllergyItems().map(a => a.primary.toLowerCase().trim())
    );
    const allergiesToRemove = new Set(
      this.existingAllergyItems()
        .filter(a => !referralAllergyNames.has(a.primary.toLowerCase().trim()))
        .map(a => a.id as number)
    );
    this.allergiesToRemove.set(allergiesToRemove);

    const referralProblemNames = new Set(
      this.referralProblemItems().map(p => p.primary.toLowerCase().trim())
    );
    const problemsToRemove = new Set(
      this.existingProblemItems()
        .filter(p => !referralProblemNames.has(p.primary.toLowerCase().trim()))
        .map(p => p.id as number)
    );
    this.problemsToRemove.set(problemsToRemove);

    const referralDiagCodes = new Set(
      this.referralDiagnosisItems().map(d => (d.id as string).toLowerCase().trim())
    );
    const diagnosisToRemove = new Set(
      this.existingDiagnosisItems()
        .filter(d => !referralDiagCodes.has((d.id as string).toLowerCase().trim()))
        .map(d => d.id as string)
    );
    this.diagnosisToRemove.set(diagnosisToRemove);
  }
}
